import './monthNavigation.css';
import React, { useMemo, useState, useEffect } from 'react';
import { getHoustonDateInfo, generateMonthYearOptions } from '../../../../helper_functions';
import { ArrowRight, ArrowLeft }from 'lucide-react';
import { useDataManager } from '../../../../contexts/dataManager';
import { useMonthNavigation } from '../../../../contexts/monthNavigationContext';
import Dropdown from '../../../common/dropdown/dropdown';

interface MonthNavigationProps {
    tableName: string;
    initialMonth?: string; // pre-seeded month from parent, bypasses context timing issue
    onMonthChange?: (monthYear: string) => void; // Add callback prop
}

const MonthNavigation: React.FC<MonthNavigationProps> = ({
    tableName,
    initialMonth,
    onMonthChange,
}) => {

    const { requestTableData, hasTableData, isTableLoading } = useDataManager();
    const { getMonthSelection, setMonthSelection } = useMonthNavigation();
    const [selectedMonthYear, setSelectedMonthYear] = useState<string>('');

    const currentMonth = useMemo(() => getHoustonDateInfo().currentMonthName, []);
    const currentYear = useMemo(() => getHoustonDateInfo().currentYearNumber, []);

    // Generate list of month+year combinations
    const monthYearOptions = useMemo(() => {
        return generateMonthYearOptions();
    }, []);

    // Set default to current month/year or get value from context/prop
    useEffect(() => {
        if (monthYearOptions.length === 0) return;

        // Priority: prop (parent-supplied initial) > context (user navigated before) > current month
        // Matches the priority in SQLTable's fetch useEffect so the dropdown and loaded data always agree.
        const savedSelection = getMonthSelection(tableName);

        if (initialMonth && monthYearOptions.find(opt => opt.value === initialMonth)) {
            // Parent explicitly supplied a month — use it (avoids parent-effect timing race).
            setSelectedMonthYear(initialMonth);
        } else if (savedSelection && monthYearOptions.find(opt => opt.value === savedSelection)) {
            // Restore saved selection from context if the user previously navigated.
            setSelectedMonthYear(savedSelection);
        } else {
            // Default to current month/year in "December 2024" format
            const currentMonthYear = `${currentMonth} ${currentYear}`;
            const currentOption = monthYearOptions.find(option => option.value === currentMonthYear);
            
            if (currentOption) {
                setSelectedMonthYear(currentMonthYear);
            } else {
                // If current month not in range, default to the most recent
                setSelectedMonthYear(monthYearOptions[0].value);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthYearOptions, tableName, initialMonth]);

    // Get current index of selected month/year
    const currentIndex = useMemo(() => {
        return monthYearOptions.findIndex(option => option.value === selectedMonthYear);
    }, [monthYearOptions, selectedMonthYear]);

    // Navigate to previous month
    const handlePrevious = () => {
        const nextIndex = currentIndex + 1; // +1 because array is reversed
        if (nextIndex < monthYearOptions.length) {
            handleMonthYearChange(monthYearOptions[nextIndex].value);
        }
    };

    // Navigate to next month
    const handleNext = () => {
        const prevIndex = currentIndex - 1; // -1 because array is reversed
        if (prevIndex >= 0) {
            handleMonthYearChange(monthYearOptions[prevIndex].value);
        }
    };

    // Check if navigation buttons should be disabled
    const isPreviousDisabled = currentIndex >= monthYearOptions.length - 1;
    const isNextDisabled = currentIndex <= 0;

    const handleMonthYearChange = (selectedValue: string) => {
        setSelectedMonthYear(selectedValue);
        setMonthSelection(tableName, selectedValue); // save to context

        const selectedOption = monthYearOptions.find(option => option.value === selectedValue);
        
        // Call the parent callback to notify of month change
        if (onMonthChange && selectedOption) {
            onMonthChange(selectedOption.label);
        }

        if (selectedOption && !hasTableData(tableName, selectedOption.label)) {
            requestTableData(tableName, selectedOption.label);
        }
    };

    // Notify parent when default month is set
    useEffect(() => {
        if (selectedMonthYear && onMonthChange && monthYearOptions.length > 0) {
            const selectedOption = monthYearOptions.find(option => option.value === selectedMonthYear);
            if (selectedOption) {
                onMonthChange(selectedOption.label);
            }
        }
    }, [selectedMonthYear, monthYearOptions]);

    const isLoading = isTableLoading(tableName);

    return (
        <div className="monthNavigation-mainContainer">
            <button 
                className="monthNavigation-navigationButton"
                onClick={handlePrevious}
                disabled={isPreviousDisabled || isLoading}
                title="Previous Month"
            >
                <ArrowLeft
                    style={{ color: (isPreviousDisabled || isLoading) ? 'var(--color-grey-medium)' : 'var(--color-blue-default)' }}
                    size={16}
                />
            </button>
            <Dropdown
                options={monthYearOptions}
                value={selectedMonthYear}
                onChange={handleMonthYearChange}
                placeholder="Select Month/Year"
                sortOrder="none"
                width={150}
                className="dropdown-inputMatch"
                isDisabled={isLoading}
            />
            <button 
                className="monthNavigation-navigationButton"
                onClick={handleNext}
                disabled={isNextDisabled || isLoading}
                title="Next Month"
            >
                <ArrowRight
                    style={{ color: (isNextDisabled || isLoading) ? 'var(--color-grey-medium)' : 'var(--color-blue-default)' }}
                    size={16}
                />
            </button>
        </div>
    );
};

export default MonthNavigation;